import React, { useEffect, useState } from "react";
import axios from "axios";
import WebApp from "@twa-dev/sdk";
import { safeOpenTelegramLink } from "../utils/linkHelpers";
import { API_URL } from "../config";
import { FaFilm, FaChevronLeft, FaSearch } from "react-icons/fa";
import { ImSpinner2 } from "react-icons/im";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import haptic from "../utils/haptic";

const PurchasedMovies = () => {
  const { userData, APIKey } = useUser();
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchMovies = async () => {
      if (!userData || !userData.userId) return;
      try {
        const response = await axios.get(
          `${API_URL}/api/user/${userData.userId}/purchased-movies`,
          {
            params: { initData: WebApp.initData },
            headers: { "x-api-key": APIKey },
          }
        );
        setMovies(response.data);
      } catch (error) {
        console.error("Error fetching purchased movies:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMovies();
  }, [userData, APIKey]);

  const filteredMovies = movies.filter((movie) =>
    (movie.name || movie.channelName)?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="bg-slate-50 dark:bg-gradient-to-br dark:from-gray-900 dark:to-black text-slate-900 dark:text-white min-h-screen pb-24 transition-colors duration-300"
    >
      <div className="container mx-auto p-4 max-w-lg space-y-5 pt-20">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => {
              haptic.tap();
              navigate(-1);
            }}
            className="p-2 bg-white dark:bg-slate-800 rounded-full shadow-sm border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition"
          >
            <FaChevronLeft className="text-slate-600 dark:text-slate-300" />
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FaFilm className="text-purple-500" /> My Library
          </h1>
        </div>

        {/* Search */}
        <div className="relative w-full mb-6">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search movies..."
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm text-sm focus:outline-none focus:border-purple-400 dark:focus:border-purple-500 transition-colors"
          />
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-20">
            <ImSpinner2 className="text-purple-500 animate-spin text-4xl" />
          </div>
        ) : filteredMovies.length > 0 ? (
          <div className="grid grid-cols-1 gap-4">
            {filteredMovies.map((movie, index) => (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                key={movie._id}
                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 rounded-2xl shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4"
              >
                <div>
                  <h3 className="font-bold text-lg mb-1">{movie.name || movie.channelName}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2">
                    {movie.description || "No description available."}
                  </p>
                  {movie.purchasedAt && (
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                      Purchased: {new Date(movie.purchasedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    haptic.tap();
                    safeOpenTelegramLink(movie.movie_url || movie.url);
                  }}
                  className="w-full sm:w-auto shrink-0 py-3 px-6 bg-[#9333EA] hover:bg-[#7e22ce] text-white rounded-xl font-bold shadow-md shadow-purple-500/20 flex items-center justify-center gap-2 transition"
                >
                  <FaFilm /> Watch Now
                </button>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <FaFilm className="text-6xl text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-700 dark:text-slate-300 mb-2">
              {searchQuery ? "No movies found" : "Your library is empty"}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              {searchQuery
                ? `No results for "${searchQuery}"`
                : "Movies you purchase will appear here."}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default PurchasedMovies;
